/**
 * @description 
 * This component handles the entire IRB form fields logic, including:
 * - Displaying questions, instructions, warnings, and document uploads.
 * - Handling validation constraints.
 * - Respecting visibility dependencies for conditional fields.
 * 
 * Key features:
 * - Detailed custom validation for document fields.
 * - Reusable "FormField" definitions and rendering logic.
 * - Progressive disclosure of fields based on user input or dependencies.
 * 
 * @dependencies
 * - react-hook-form: Handles form state and validations.
 * - headlessui (Listbox, RadioGroup): For styled dropdowns & radio groups.
 * 
 * @notes
 * - The main fix is in the validateDocument function to avoid `instanceof File` pitfalls.
 * - We now accept objects with {id,file_path} or recognized file-like properties.
 */

import React, { useEffect } from 'react';
import { useForm, FieldErrorsImpl } from 'react-hook-form';
import { Listbox, RadioGroup } from '@headlessui/react';

/** Interfaces, form definitions, and dependency checks. */
export interface FormField {
  itemType: 'question' | 'warning' | 'instruction' | 'document';
  name?: string;
  label: string;
  fieldType?: string;
  options?: string[];
  placeholder?: string;
  validation?: Record<string, any>;
  dependency?: Record<string, any>;
  rows?: number;
  documentType?: string; 
  allowMultiple?: boolean;
}

export interface FormSection {
  heading: string;
  description?: string;
  items: FormField[];
}

export type FormData = {
  [key: string]: any;
};

/**
 * Extended File interface to include document metadata properties
 */
interface DocumentFile extends File {
  id?: string;
  file_path?: string;
  isUploading?: boolean;
  filename?: string; // Add filename for documents loaded from the database
}

/**
 * Revised custom validation function for document fields.
 * We return TRUE if:
 *  - single doc: (value instanceof File) OR has .id or .file_path
 *  - multiple docs: array length > 0, each item meets above check
 * Otherwise we return FALSE so that the form sets an error.
 */
export const validateDocument = (value: any): boolean => {
  // Null or undefined => invalid
  if (!value) return false;

  // If array => ensure at least 1 item and each item is valid
  if (Array.isArray(value)) {
    if (value.length === 0) return false;
    return value.every((item) => {
      // If item is a File
      if (item instanceof File) return true;
      // Or if item is an object with an id or file_path
      if (item && (item.id || item.file_path)) return true;
      return false;
    });
  }

  // Single file scenario
  if (value instanceof File) return true;
  if (value && (value.id || value.file_path)) return true;

  // fallback
  return false;
};

/** Full formSections with all the sections included. */
export const formSections: FormSection[] = [
  {
    heading: 'Basic Study Protocol Information',
    items: [
      {
        itemType: 'question',
        name: 'ref_name',
        label: 'Enter a <b>reference name</b> for this submission:',
        fieldType: 'text',
        placeholder: 'short version of protocol name for easy reference, e.g., ABC study',
        validation: {
          required: true,
          maxLength: 30,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'protocol_title',
        label: 'Enter the <b>full title</b> of your research protocol:',
        fieldType: 'text',
        placeholder: 'e.g., Assessment of [Variable/Condition] in [Population/Context]',
        validation: {
          required: true,
          maxLength: 300,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'sponsor_name',
        label: 'What institution is sponsoring this study?',
        fieldType: 'text',
        placeholder: 'e.g., ACME Inc.',
        validation: {
          required: true,
          maxLength: 100,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'is_multi_site',
        label:
          'Is this a multi-site study conducted at two or more geographically distinct physical locations?<br><small>(answer "no" if none of the study procedures take place face-to-face)</small>',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'is_multi_pi',
        label:
          'Is there more than one Principal Investigator (PI) for this research?<br><small>e.g., a lead PI and a site PI for each geographically distinct site</small>',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'is_multi_site', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'is_central_irb',
        label: 'Has the Sponsor or CRO designated this IRB as the central IRB for this study?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'is_multi_pi', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'cro_smo_involvement',
        label: 'Is a Contract Research Organization (CRO) and/or Site Management Organization (SMO) involved in the research?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'cro_smo_name',
        label: 'Enter the name of the CRO and/or SMO:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 200,
          pattern: /^.*$/,
        },
        dependency: { field: 'cro_smo_involvement', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'is_non-us',
        label: 'Is any of the proposed research going to be conducted outside the United States?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'is_fed_funded',
        label: 'Is this research funded, supported, or conducted by a United States federal department or agency?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
      },
      {
        itemType: 'instruction',
        label:
          'You will need to upload written documentation of the Federal Wide Assurance (FWA) for each organization involved with the research in the document upload section of this form.',
        dependency: { field: 'is_fed_funded', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'fed_funding_agency',
        label:
          'Select the federal department or agency funding the research (you may select more than one, if applicable):',
        fieldType: 'checkboxes',
        options: [
          'Agency for International Development',
          'Department of Agriculture',
          'Bureau of Prisons, Department of Justice',
          'Central Intelligence Agency',
          'Consumer Product Safety Commission',
          'Corporation for National and Community Service',
          'Department of Commerce',
          'Department of Defense',
          'Department of Education',
          'Department of Energy',
          'Department of Health and Human Services',
          'Centers for Disease Control',
          'Environmental Protection Agency',
          'Federal Bureau of Investigation, Department of Justice',
          'National Aeronautics and Space Administration',
          'National Institutes of Health',
          'Department of Homeland Security',
          'Department of Housing and Urban Development',
          'Office of Justice Program, Department of Justice',
          'Office of the Director of National Intelligence',
          'Social Security Administration',
          'Department of Transportation',
          'Department of Veterans Affairs',
        ],
        validation: { required: true },
        dependency: { field: 'is_fed_funded', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'fda_submit',
        label:
          'Will you or others be submitting data from this research to the FDA or hold data for inspection by the FDA?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'fda_research_type',
        label:
          'The category of FDA-regulated product being investigated or otherwise evaluated through this research is (select all that apply):',
        fieldType: 'checkboxes',
        options: ['Medical device', 'Drug/Biologic', 'Food', 'Radioactive material', 'Other'],
        validation: { required: true },
        dependency: { field: 'fda_submit', value: 'Yes' },
      },
      {
        itemType: 'instruction',
        label: 'Select "medical device" if your investigational product is an In Vitro Diagnostic or Software as a Medical Device.',
        dependency: { field: 'fda_submit', value: 'Yes' }
      },
      {
        itemType: 'question',
        name: 'fda_research_type_other',
        label: 'If Other, describe the type of FDA-regulated research:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 500,
          pattern: /^.*$/,
        },
        dependency: { all: [{field: 'fda_research_type', value: 'Other' }, {field: 'fda_submit', value: 'Yes'}]},
      },
      {
        itemType: 'question',
        name: 'ind_conduct',
        label: 'Are you conducting the research under an Investigational New Drug Application (IND)?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { all: [{field: 'fda_research_type', value: 'Drug/Biologic' }, {field: 'fda_submit', value: 'Yes'}]},
      },
      {
        itemType: 'question',
        name: 'ide_conduct',
        label: 'Are you conducting the research under an Investigation Device Exemption (IDE)?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { all: [{field: 'fda_research_type', value: 'Medical device' }, {field: 'fda_submit', value: 'Yes'}]},
      },
      {
        itemType: 'question',
        name: 'nsr_conduct',
        label: 'Are you conducting the research under the Abbreviated IDE (NSR) requirements?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { all: [{field: 'fda_research_type', value: 'Medical device' }, {field: 'fda_submit', value: 'Yes'}]},
      },
      {
        itemType: 'question',
        name: 'evaluate_fda_device',
        label: 'Are you conducting the research to evaluate a previously FDA-cleared or FDA-approved device or drug or biologic?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
    },
      {
        itemType: 'question',
        name: 'vulnerable_populations',
        label: 'Are any of the populations below involved in your research? Select all that apply:',
        fieldType: 'dropdown-multiple',
        options: [
          'Subjects who are adults unable to consent',
          'Subjects who are children',
          'Children who are wards of the state',
          'Non-viable neonates',
          'Neonates of uncertain viability',
          'Subjects who are prisoners',
          'Subjects who are pregnant at enrollment',
          'Subjects who become pregnant while on study',
          'None of the above',
        ],
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'studentsoremployees_epa_genetransfer',
        label:
          'Does the research involve ANY of the following: (1) research participants who are students or employees of the investigators or sponsor; (2) collection of data to be submitted to the Environmental Protection Agency; (3) any form of human gene transfer as described in Section III-C of the NIH Guidelines?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'subjects_students_employees',
        label:
          'Will the research involve subjects who are students or employees of the investigators?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'studentsoremployees_epa_genetransfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'epa_submit',
        label: 'Will you or others submit data from this research to the US Environmental Protection Agency (EPA)?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'studentsoremployees_epa_genetransfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'human_gene_transfer',
        label:
          'Does this research involve any form of human gene transfer as described in Section III-C of the NIH Guidelines?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'studentsoremployees_epa_genetransfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'special_committees',
        label:
          'To the best of your knowledge, does this study require an independent Data Monitoring, Data Safety Monitoring or Endpoint Adjudication Committee per federal regulations or sponsor policy?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'dm_committee',
        label:
          'Does this study require an independent Data Monitoring Committee / Data Safety Monitoring Board?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'special_committees', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'dm_service_provider',
        label: 'Has a service provider already been selected for the Data Monitoring Committee?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'special_committees', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'dm_service_provider_name',
        label: 'If Yes, provide the name of the service provider for the Data Monitoring Committee:',
        fieldType: 'text',
        placeholder: 'DMC service provider name',
        validation: {
          required: true,
          maxLength: 200,
          pattern: /^.*$/,
        },
        dependency: { field: 'special_committees', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'endpoint_committee',
        label: 'Does this study require an independent Endpoint Adjudication Committee?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'special_committees', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'endpoint_service_provider',
        label: 'Has a service provider already been selected for the Endpoint Adjudication Committee?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'special_committees', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'endpoint_service_provider_name',
        label: 'If Yes, provide the name of the service provider for the Endpoint Adjudication Committee:',
        fieldType: 'text',
        placeholder: 'EAC service provider name',
        validation: {
          required: true,
          maxLength: 200,
          pattern: /^.*$/,
        },
        dependency: { field: 'special_committees', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'pii_or_biospecimen',
        label: 'Does this research involve the collection of identifiable private information or identifiable biospecimens during study procedures?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'secondary_research',
        label: 'Select one option regarding secondary research:',
        fieldType: 'radio',
        options: [
          'There is a possibility that identifiers might be removed from the identifiable private information or biospecimens, and after such removal, the information/biospecimens may be used for future research or distributed to another investigator without additional informed consent.',
          'Subject information or identifiable biospecimens collected as part of the research, even if identifiers are removed, will not be used or distributed for future research.',
        ],
        validation: { required: true },
        dependency: { field: 'pii_or_biospecimen', value: 'Yes' },
    },
    ],
  },
  {
    heading: 'Informed Consent, Participant Compensation, Privacy, and Confidentiality',
    items: [
        {
            itemType: 'question',
            name: 'informed_consent',
            label: 'Will study participants or their representatives provide informed consent to take part in this research?',
            fieldType: 'radio',
            options: ['Yes', 'No'],
            validation: { required: true }, 
          },
          {
            itemType: 'question',
            name: 'waiver_request_reason',
            label: 'It appears you are requesting a waiver of informed consent. Describe the reason for the waiver request:',
            fieldType: 'text',
            rows: 3,
            validation: { 
                required: true,
                maxLength: 1000,
                pattern: /^[\s\S]*$/,
            },
            dependency: { field: 'informed_consent', value: 'No' },
          },  
        {
        itemType: 'question',
        name: 'consent_setting',
        label: 'Select the setting of the consent process (choose one):',
        fieldType: 'dropdown-single',
        options: [
          'Private room',
          'Waiting room',
          'Over the phone',
          'Online',
          'Open Ward',
          'Emergency setting',
          'Group setting',
          'In public',
          'Other',
        ],
        validation: { required: true },
        dependency: { field: 'informed_consent', value: 'Yes' },
      },
      {
        itemType: 'instruction',
        label:
          'The research team must:'+
          '<ul><li> - Give the person providing informed consent as much time as needed to decide.</li>'+
          '<li>- If more time is needed than allowed by the research design, do not enroll the participant.</li>'+
          '<li>- Evaluate whether the person is experiencing time pressure and, if so, do not enroll. </li>'+
          '<li>- Evaluate for coercion or undue influence, and do not enroll if detected.</li>'+
          '<li>- Invite and answer questions about the research.</li></ul>',
        dependency: { field: 'informed_consent', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'consent_process_complete',
        label: 'Will the research team perform all the above?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'informed_consent', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'written_consent',
        label: 'Will subjects or their representatives sign a consent form physically or electronically?<br><small>Answer "yes" even if consent will be provided verbally (e.g., over the phone), but a study operator records the consent physically or electronically for each participant.</small>',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'informed_consent', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'signature_method',
        label: 'How will signatures be obtained?',
        fieldType: 'dropdown-single',
        options: ['Wet Ink', 'Electronic', 'Both'],
        validation: { required: true },
        dependency: {all: [{field: 'written_consent', value: 'Yes'}, {field: 'informed_consent', value: 'Yes'}]},
      },
      {
        itemType: 'question',
        name: 'fda_part11_compliance',
        label:
          'Is the signature documentation on the online consent form FDA Part 11 compliant as a legal signature?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: {all: [{field: 'signature_method', value: 'Electronic'}, {field: 'informed_consent', value: 'Yes'}, {field: 'fda_submit', value: 'Yes'}]},
      },
      {
        itemType: 'instruction',
        label:
          'Consent Documentation Process: The research team must:'+
          '<ul><li>- Give adequate opportunity to read, or listen to, the consent document before signing.</li>'+
          '<li>- Ensure the consent document is signed and dated by the person providing consent or their representative, or that the verbal consent is recorded electronically.</li>'+
          '<li>- Provide a signed and dated copy of the consent document to the person.</li>'+
          '<li>- If the person cannot read, have an impartial witness present who signs and dates the document.</li></ul>',
        dependency: { field: 'informed_consent', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'consent_form_processing_complete',
        label: 'Will the research team perform all the above steps?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'informed_consent', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'subject_payment',
        label:
          'Will you compensate subjects for participation (including reimbursements such as travel, parking, meals, etc.)?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
      },
      {
        itemType: 'warning',
        label:
          'Please ensure that you have incorporated details about participant compensation into the study protocol & payment language in the consent form uploaded with this application!',
        dependency: { field: 'subject_payment', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'privacy_private_setting',
        label:
          'Will you or others perform all study procedures involving participants in a private setting?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'privacy_procedures',
        label: 'Describe what procedures will be followed to protect the privacy of participants:',
        fieldType: 'text',
        rows: 3,
        placeholder: 'Describe procedures to protect participant privacy',
        validation: {
          required: true,
          maxLength: 5000,
          pattern: /^[\s\S]*$/,
        },
        dependency: { field: 'privacy_private_setting', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'confidentiality_hipaa_compliance',
        label: 'Will your organization be subject to and in compliance with HIPAA for the duration of the study?<br><small>(if yes, you can skip the questions that will otherwise pop up below about confidentiality procedures)</small>',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'confidentiality_secure_storage',
        label:
          'Will you maintain paper records and electronic equipment containing confidential information in a physically secure location?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'confidentiality_hipaa_compliance', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'confidentiality_staff_training',
        label: 'Will staff be trained on confidentiality procedures?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'confidentiality_hipaa_compliance', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'confidentiality_access_limit',
        label: 'Will you limit access to confidentiality data on a need-to-know basis?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'confidentiality_hipaa_compliance', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'confidentiality_remove_identifiers',
        label: 'Will you remove identifiers as soon as feasible?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'confidentiality_hipaa_compliance', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'confidentiality_encrypt_storage',
        label:
          'Will you encrypt confidential data stored electronically? <br><small>(desktop, servers, mobile devices, removable media, etc.)</small>',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'confidentiality_hipaa_compliance', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'confidentiality_encrypt_transmission',
        label: 'Will you encrypt confidential data transmitted over the Internet (including email)?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'confidentiality_hipaa_compliance', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'confidentiality_additional_methods',
        label: 'Describe any additional procedures to protect confidentiality (optional):',
        fieldType: 'text',
        rows: 3,
        placeholder: '(optional) Describe additional confidentiality procedures',
        validation: {
          required: false,
          maxLength: 5000,
          pattern: /^[\s\S]*$/,
        },
        dependency: { field: 'confidentiality_hipaa_compliance', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'hipaa_waiver',
        label: 'Are you requesting the IRB to consider a full or partial waiver of HIPAA authorization as part of this study?<br><small>answer "yes"if your study requires you to access Protected Health Information (PHI) without explicit and full participant authorization to access/use PHI</small>',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'hipaa_waiver_option',
        label:
          'Which of the following HIPAA waiver options are you requesting the IRB to consider as part of this study?',
        fieldType: 'radio',
        options: [
          'Partial waiver of authorization for access to records for subject recruitment or screening<br><small>(i.e., access PHI from patient records before consent to identify, screen, or recruit potential research participants)</small>',
          'Partial waiver of authorization for waiver of signing an authorization form<br><small>(i.e., use or disclose PHI with verbal consent,without obtaining the participant’s signature on a formal HIPAA authorization form)</small>',
          'Full waiver of authorization<br><small>(i.e., access, use, or disclose participant PHI without verbal or written consent)</small>',
        ],
        validation: { required: true },
        dependency: { field: 'hipaa_waiver', value: 'Yes' },
      },
      {
        itemType: 'warning',
        label:
          'By submitting this form, I confirm that:'+
          '<ul><li>- The research team will comply with HIPAA to secure protected health information.</li>'+
          '<li>- The research team will use/disclose information only as allowed by HIPAA.</li></ul>',
        dependency: {field: 'hipaa_waiver', value: 'Yes'},
      },
      {
        itemType: 'question',
        name: 'hipaa_identifiers',
        label: 'Describe or list the identifiers planned to be used or disclosed:',
        fieldType: 'text',
        rows: 3,
        validation: {
          required: true,
          maxLength: 1000,
          pattern: /^[\s\S]*$/,
        },
        dependency: { field: 'hipaa_waiver', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'hipaa_necessity',
        label: 'Explain why access to the protected health information is necessary:',
        fieldType: 'text',
        rows: 3,
        validation: {
          required: true,
          maxLength: 1000,
          pattern: /^[\s\S]*$/,
        },
        dependency: { field: 'hipaa_waiver', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'hipaa_destroy_plan',
        label: 'Describe the plan to destroy the identifiers (or justify their retention):',
        fieldType: 'text',
        rows: 3,
        validation: {
          required: true,
          maxLength: 1000,
          pattern: /^[\s\S]*$/,
        },
        dependency: { field: 'hipaa_waiver', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'hipaa_waiver_justification',
        label: 'Explain why the research could not practicably be conducted without the waiver:',
        fieldType: 'text',
        rows: 3,
        validation: {
          required: true,
          maxLength: 1000,
          pattern: /^[\s\S]*$/,
        },
        dependency: { field: 'hipaa_waiver', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'hipaa_without_access',
        label:
          'Explain why the research could not practicably be conducted without access to and use of the protected health information:',
        fieldType: 'text',
        rows: 3,
        validation: {
          required: true,
          maxLength: 1000,
          pattern: /^[\s\S]*$/,
        },
        dependency: { field: 'hipaa_waiver', value: 'Yes' },
      },
    ],
  },
  {
    heading: 'Principal Investigator & Research Team Information',
    description:
      'Add information about the lead principal investigator for the study below.',
    items: [
      {
        itemType: 'question',
        name: 'pi_first_name',
        label: 'PI First Name:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 100,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'pi_last_name',
        label: 'PI Last Name:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 100,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'pi_degree',
        label: 'PI\'s highest degree:',
        fieldType: 'dropdown-single',
        options: ['MD/PhD', 'MD', 'PhD', 'Bachelor\'s', 'Master\'s', 'Some College', 'High School'],
        validation: {
          required: true,
        },
      },
      {
        itemType: 'question',
        name: 'pi_organization',
        label: 'PI\'s primary affiliation - company/institution:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 200,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'pi_email',
        label: 'PI\'s work email:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 254,
          pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        },
      },
      {
        itemType: 'question',
        name: 'pi_phone',
        label: 'PI US phone (10 digits, no country code):',
        fieldType: 'text',
        validation: {
          required: true,
          minLength: 10,
          maxLength: 10,
          pattern: /^[0-9]+$/,
        },
      },
      {
        itemType: 'question',
        name: 'pi_address_line_1',
        label: 'PI Address Line 1:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 200,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'pi_address_line_2',
        label: 'PI Address Line 2:',
        fieldType: 'text',
        validation: {
          required: false,
          maxLength: 200,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'pi_city',
        label: 'PI\'s city:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 100,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'pi_state',
        label: 'PI\'s state:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 100,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'pi_zip_code',
        label: 'PI\'s zip code:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 5,
          pattern: /^[0-9]+$/,
        },
      },
      {
        itemType: 'question',
        name: 'pi_country',
        label: 'Enter the PI\'s country:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 100,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'pi_profile_links',
        label: 'PI\'s profile/bio links:',
        fieldType: 'text',
        placeholder: 'input at least one, and as many, links as you\'d like - CV, department bio, LinkedIn, Google Scholar, personal website, lab website, etc.',
        rows: 3,
        validation: {
          required: true,
          maxLength: 1000,
          pattern: /^[\s\S]*$/,
        },
      },
      {
        itemType: 'question',
        name: 'is_pi_physician',
        label: 'Is the PI a practicing physician?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'pi_npi_number',
        label: 'Enter the PI\'s NPI number:<br><small>You can look it up <b><span style="color: blue;"><a href="https://npiregistry.cms.hhs.gov/search" target="_blank" rel="noopener noreferrer">HERE</a></span></b></small>',
        fieldType: 'text',
        validation: {
          required: true,
          minLength: 10,
          maxLength: 10,
          pattern: /^[0-9]+$/,
        },
        dependency: { field: 'is_pi_physician', value: 'Yes' },
      },
      {
        itemType: 'question',
        name:"pi_specialty",
        label: 'Select the PI\'s specialty (you may select multiple options):',
        fieldType: 'dropdown-multiple',
        options: ['Allergy and Immunology', 'Anesthesiology', 'Cardiology', 'Dermatology', 'Emergency Medicine', 'Endocrinology', 'Family Medicine', 'Gastroenterology', 'General Surgery', 'Geriatrics', 'Hematology', 'Infectious Disease', 'Internal Medicine', 'Medical Genetics', 'Nephrology', 'Neurology', 'Neurosurgery', 'Obstetrics and Gynecology', 'Oncology', 'Ophthalmology', 'Orthopedic Surgery', 'Otolaryngology (ENT)', 'Pathology', 'Pediatrics', 'Physical Medicine and Rehabilitation', 'Plastic Surgery', 'Preventive Medicine', 'Primary Care', 'Psychiatry', 'Pulmonology', 'Radiology', 'Radiation Oncology', 'Rheumatology', 'Urology', 'Vascular Surgery'],
        validation: {
          required: true,
        },
        dependency: { field: 'is_pi_physician', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'research_team_summary',
        label:
          'Provide a descriptive summary of the research team that will be operationalizing this study and why they\'re qualified to run this study under the PI\'s leadership.<br><small>Include information about any physician and non-physician sub- or co-investigators, research coordinators, and other research staff. Feel free to include links to research team bios or provide them using the upload functionality of this submission if you feel it will help make your case.</small>',
        fieldType: 'text',
        placeholder: 'Describe the composition and qualifications of the research team',
        rows: 9,
        validation: {
          required: true,
          maxLength: 5000,
          pattern: /^[\s\S]*$/,
        },
      },
      {
        itemType: 'question',
        name: 'training_certifications',
        label:
          'The PI and research study staff must have human subjects protections training for the entire duration of the study - select the training(s) used by your study\'s team:',
        fieldType: 'dropdown-multiple',
        options: [
          'ACRP Certified Clinical Investigator Training',
          'CenterWatch: Protecting Study Volunteers in Research',
          'Collaborative IRB Training Initiative (CITI)',
          'DIA Certified Investigator (CCI)',
          'SOCRA Clinical Research Professional (CRP)',
          'Tri-Council Policy Statement online training (TCPS)',
          'Human Research Protection Foundational Training (OHRP)',
          'PHRP Online Training',
          'Other',
        ],
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'training_other_description',
        label:
          'Describe the training (and confirm it included human subject protections training):',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 500,
          pattern: /^.*$/,
        },
        dependency: { field: 'training_certifications', value: 'Other' },
      },
      {
        itemType: 'question',
        name: 'admin_pending_action',
        label: 'Is there any pending administrative action or investigation involving the PI or other study personnel?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'admin_action_individuals_list',
        label: 'List the individual(s) involved and their role(s) in the research:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 500,
          pattern: /^.*$/,
        },
        dependency: { field: 'admin_pending_action', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'admin_action_start_dates',
        label: 'Provide the approximate start date(s) of occurrence:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 100,
          pattern: /^.*$/,
        },
        dependency: { field: 'admin_pending_action', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'admin_action_issuing_entities',
        label: 'Name the entity(ies) issuing the action:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 200,
          pattern: /^.*$/,
        },
        dependency: { field: 'admin_pending_action', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'admin_action_corrective_plan',
        label:
          'Provide a detailed history and any management or corrective action plan currently in place:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 1000,
          pattern: /^.*$/,
        },
        dependency: { field: 'admin_pending_action', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'admin_action_documents',
        label:
          'Will all related documents including resolution steps be shared with this IRB via this study\'s document upload functionality?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'admin_pending_action', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'research_locations_confirmation',
        label:
          'Please confirm that all geographical locations where study participants will be seen or research will take place—and their respective functions associated with the proposed study—will be included in the study protocol or other documents uploaded with this study submission. <br><small>Include the company/institution/organization name of the site, street address, and study-related resources associated with the locations.</small>',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'location_pi_oversight',
        label: 'Explain how the PI will provide adequate oversight of the research location(s):',
        fieldType: 'text',
        placeholder: 'describe how the PI will provide oversight',
        rows: 4,
        validation: {
          required: true,
          maxLength: 2000,
          pattern: /^[\s\S]*$/,
        },
      },
      {
        itemType: 'question',
        name: 'financial_conflict_disclosure',
        label:
          'Does the Principal Investigator (PI), the PI\'s immediate family, or any other research personnel or their immediate families, have any of the following financial interests in any entity that is sponsoring the research, or an entity that is manufacturing the product or service being tested? <br><small><ol><li>(1) Any remuneration from the entity in the previous twelve months that exceeds $5,000, when aggregated for the individual and their immediate family;</li><li>(2) Any equity interest in the entity;</li><li>(3) Any intellectual property rights and interests;</li><li>(4) Any governance or executive relationship with the entity.</li></ol></small>',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'conflict_management_plan',
        label:
          'Describe the financial interests in great detail, and describe why, including a conflict management plan as appropriate in your description, for why these financial interests will NOT adversely affect the ethical conduct of the study by biasing the consent process or otherwise biasing the integrity of the research.',
        fieldType: 'text',
        placeholder: 'description of potential conflicts and conflict management plan',
        rows: 6,
        validation: {
          required: true,
          maxLength: 5000,
          pattern: /^[\s\S]*$/,
        },
        dependency: { field: 'financial_conflict_disclosure', value: 'Yes' },
      },
    ],
  },
  {
    heading: 'Prior IRB Review & IRB Transfer',
    items: [
      {
        itemType: 'question',
        name: 'prior_irb_review',
        label:
          'Has another IRB reviewed this research or site and decided to table, defer, disapprove, suspend, terminate, or decline to approve it?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'prior_irb_explanation',
        label: 'If Yes, provide a detailed explanation of the previous reviews by other IRBs:',
        fieldType: 'text',
        rows: 6,
        validation: {
          required: true,
          maxLength: 5000,
          pattern: /^[\s\S]*$/,
        },
        dependency: { field: 'prior_irb_review', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'irb_transfer',
        label: 'Are you transferring IRB oversight from another IRB to this IRB?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_prev_irb_name',
        label: 'Name of Previous IRB:',
        fieldType: 'text',
        placeholder: 'Enter IRB name',
        validation: {
          required: true,
          maxLength: 200,
          pattern: /^.*$/,
        },
        dependency: { field: 'irb_transfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_prev_irb_website',
        label: 'Website of previous IRB:',
        fieldType: 'text',
        placeholder: 'Enter full website URL',
        validation: {
          required: true,
          maxLength: 200,
          pattern: /^(https?:\/\/.*)$/,
        },
        dependency: { field: 'irb_transfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_prev_irb_initial_date',
        label: 'Date of initial approval by the previous IRB:',
        fieldType: 'text',
        placeholder: 'use MM/DD/YYYY format',
        validation: {
          required: true,
          maxLength: 10,
          pattern: /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/[0-9]{4}$/,
        },
        dependency: { field: 'irb_transfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_prev_irb_expiry_date',
        label: 'Date approval expires:',
        fieldType: 'text',
        placeholder: 'use MM/DD/YYYY format',
        validation: {
          required: true,
          maxLength: 10,
          pattern: /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/[0-9]{4}$/,
        },
        dependency: { field: 'irb_transfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_transfer_reason',
        label: 'Reason for Transfer to Tempus IRB:',
        fieldType: 'text',
        rows: 3,
        placeholder: 'Enter reason for transfer',
        validation: {
          required: true,
          maxLength: 1000,
          pattern: /^[\s\S]*$/,
        },
        dependency: { field: 'irb_transfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_study_begun',
        label: 'Has the study begun?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'irb_transfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_enroll_more',
        label: 'Do you intend to enroll any more subjects?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'irb_transfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_subjects_active',
        label: 'Are any subjects still in active study procedures?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'irb_transfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_study_completed',
        label:
          'Is the study completed, but not yet closed out by the sponsor? (Have all subjects at your site completed all study procedures?)',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'irb_transfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_new_risk_info',
        label:
          'Has any new risk or benefit information become available that was not reported to the previous IRB?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'irb_transfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_new_risk_info_details',
        label: 'Submit the new risk or benefit information for review:',
        fieldType: 'text',
        rows: 3,
        placeholder: 'describe new risk or benefit information',
        validation: {
          required: true,
          maxLength: 5000,
          pattern: /^[\s\S]*$/,
        },
        dependency: {
          all: [
            { field: 'irb_transfer_new_risk_info', value: 'Yes' },
            { field: 'irb_transfer', value: 'Yes' },
          ],
        },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_unanticipated_problems',
        label:
          'Have there been any unanticipated problems related to this research? <br><small>Unanticipated problems include any incident, experience, or outcome that is unexpected, possibly related to research participation, and suggests subjects or others may be at greater risk than previously known.</small>',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'irb_transfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_unanticipated_problems_details',
        label: 'Describe the problem(s) and action(s) taken, if any:',
        fieldType: 'text',
        rows: 4,
        placeholder: 'describe unanticipated problems and actions taken',
        validation: {
          required: true,
          maxLength: 5000,
          pattern: /^[\s\S]*$/,
        },
        dependency: {
          all: [
            { field: 'irb_transfer_unanticipated_problems', value: 'Yes' },
            { field: 'irb_transfer', value: 'Yes' },
          ],
        },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_subject_complaints',
        label: 'Have there been any subject complaints related to this research?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'irb_transfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_subject_complaints_details',
        label: 'Describe the subject complaint(s) and action(s) taken to address them:',
        fieldType: 'text',
        rows: 3,
        placeholder: 'describe subject complaints and actions taken',
        validation: {
          required: true,
          maxLength: 5000,
          pattern: /^[\s\S]*$/,
        },
        dependency: {
          all: [
            { field: 'irb_transfer_subject_complaints', value: 'Yes' },
            { field: 'irb_transfer', value: 'Yes' },
          ],
        },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_prev_irb_suspension',
        label: 'Did the previous IRB ever suspend or terminate this research at one or more sites?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'irb_transfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_prev_irb_suspension_details',
        label:
          'Provide information on the reason, the steps taken to resume the research, and copies of any IRB correspondence related to the suspension:',
        fieldType: 'text',
        rows: 4,
        placeholder: 'provide details of IRB suspension or termination',
        validation: {
          required: true,
          maxLength: 2000,
          pattern: /^[\s\S]*$/,
        },
        dependency: {
          all: [
            { field: 'irb_transfer_prev_irb_suspension', value: 'Yes' },
            { field: 'irb_transfer', value: 'Yes' },
          ],
        },
      },
    ],
  },
  {
    heading: 'Billing Contact Information',
    description: 'Provide the best contact for billing purposes. An invoice will be sent when this form is submitted. Payments can be made via ACH or credit card, and payments must be completed before the IRB\'s review decision is released.',
    items: [
      {
        itemType: 'question',
        name: 'billing_contact_first_name',
        label: 'First Name:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 100,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'billing_contact_last_name',
        label: 'Last Name:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 100,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'invoice_email',
        label:
          'What\'s the billing contact\'s email address?',
        fieldType: 'text',
        placeholder: 'Accounts Payable contact email',
        validation: {
          required: true,
          maxLength: 254,
          pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        },
      },
      {
        itemType: 'question',
        name: 'billing_contact_phone',
        label: 'Phone:',
        fieldType: 'text',
        placeholder: '10 digit phone number',
        validation: {
          required: true,
          minLength: 10,
          maxLength: 10,
          pattern: /^[0-9()+-\s]+$/,
        },
      },
    ],
  },
  {
    heading: 'Submission Documents Upload',
    description: 'Upload all documents required for review. Do not upload any documents with password protection.',
    items: [
      {
        itemType: 'document',
        name: 'doc_protocol',
        label: 'Research Study Protocol <br><small>The protocol must include a clear description of study objectives, methodology, participant selection criteria, informed consent procedures, data collection and analysis plans, risks and benefits assessment, privacy and confidentiality measures, study timelines, and plans for data safety monitoring, ensuring compliance with ethical standards and regulatory requirements.</small>',
        documentType: '.pdf',
        validation: { 
            validate: {
            docCheck: (val: any) => 
              validateDocument(val) || 'Please upload a Protocol Document.',
          },
        },
      },
      {
        itemType: 'document',
        name: 'doc_human_subject_training',
        label: 'Human Subject Training Documentation for all study personnel<br><small>Upload training certificates for all study team members.</small>',
        documentType: '.pdf', 
        validation: { 
          validate: {
            docCheck: (val: any) => 
              validateDocument(val) || 'Please upload at least one Human Subject Training Document.',
          },
        },
        allowMultiple: true, 
      },
      {
        itemType: 'question',
        name: 'doc_selection',
        label:
          'Select the additional document type(s) you\'d like to upload:<br><small><i>Note:</i> If any of the document types in the list below are applicable to or used in your study, you <strong>must</strong> upload them.</small>',
        fieldType: 'dropdown-multiple',
        options: ['Study Personnel Resumes/CVs', 'Investigator\'s Brochure', 'Participant Recruitment Materials', 'Data Collection Tools (CRFs / Questionnaires /Surveys)', 'Additional Document Types', 'None of these documents apply to this study'],
        validation: { required: true },
      },
      {
        itemType: 'document',
        name: 'doc_consent_form',
        label: 'Informed Consent Form<br><small>The consent form must clearly and accurately communicate the study\'s purpose, procedures, risks and potential benefits, compensation information,confidentiality protections, participant rights (including voluntary participation and the right to withdraw), contact information for questions, and acknowledgment of consent through participant signature or documented consent process, in compliance with regulatory and ethical guidelines.</small>',
        documentType: '.pdf,.doc,.docx',
        validation: { 
          validate: {
            docCheck: (val: any) => 
              validateDocument(val) || 'Please upload an Informed Consent Form.',
          },
        },
        dependency: { field: 'informed_consent', value: 'Yes' },
      },
      {
        itemType: 'document',
        name: 'doc_fwa_documentation',
        label: 'Federal Wide Assurance (FWA) Documentation<br><small>Documentation of your institution\'s FWA number and expiration date. Easiest way to provide this is to go to the <b><span style="color: blue;"><a href="https://ohrp.cit.nih.gov/search/FwaDtl.aspx" target="_blank" rel="noopener noreferrer">OHRP FWA search tool</a></span></b>, find your institution\'s assurance page, and get a screenshot.</small>',
        documentType: '.pdf,.jpg,.jpeg,.png',
        validation: { 
          validate: {
            docCheck: (val: any) => 
              validateDocument(val) || 'Please upload Federalwide Assurance (FWA) Documentation.',
          },
        },
        dependency: { field: 'is_fed_funded', value: 'Yes' },
      },
      {
        itemType: 'document',
        name: 'doc_irb_auth_agreement',
        label: 'IRB Authorization Agreement. <br><small>We recommend using <b><span style="color: blue;"><a href="https://www.hhs.gov/ohrp/sites/default/files/ohrp/assurances/forms/irbauthorizpdf.pdf" target="_blank" rel="noopener noreferrer">this simple form agreement from HHS</a></span></b>. Ensure it is signed by your institution\'s signatory. Tempus IRB\'s registration number is IRB00014644.</small>',
        documentType: '.pdf',
        validation: { 
          validate: {
            docCheck: (val: any) => 
              validateDocument(val) || 'Please upload IRB Authorization Agreement.',
          },
        },
        dependency: { field: 'is_fed_funded', value: 'Yes' },
      },
      {
        itemType: 'document',
        name: 'doc_administrative_action',
        label: 'Administrative Action Documentation',
        documentType: '.pdf,.doc,.docx',
        validation: { 
          validate: {
            docCheck: (val: any) => 
              validateDocument(val) || 'Please upload Administrative Action Documentation.',
          },
        },
        dependency: { 
          all: [
            { field: 'admin_pending_action', value: 'Yes' },
            { field: 'admin_action_documents', value: 'Yes' }
          ]
        },
      },
      {
        itemType: 'document',
        name: 'doc_study_personnel_resumes',
        label: 'Study Personnel Resumes/CVs<br><small>Upload resumes or CVs for key study team personnel.</small>',
        documentType: '.pdf',
        validation: { 
          validate: {
            docCheck: (val: any) => 
              validateDocument(val) || 'Please upload at least one Study Personnel Resume/CV.',
          },
        },
        allowMultiple: true,
        dependency: { field: 'doc_selection', value: 'Study Personnel Resumes/CVs' },
      },
      {
        itemType: 'document',
        name: 'doc_investigator_brochure',
        label: 'Investigator\'s Brochure',
        documentType: '.pdf',
        validation: { 
          validate: {
            docCheck: (val: any) => 
              validateDocument(val) || 'Please upload an Investigator\'s Brochure.',
          },
        },
        dependency: { field: 'doc_selection', value: 'Investigator\'s Brochure' },
      },
      {
        itemType: 'document',
        name: 'doc_recruitment_materials',
        label: 'Participant Recruitment Materials<br><small>Examples: recruitment scripts, flyers, posters, brochures, etc.</small>',
        documentType: '.pdf,.doc,.docx,.ppt,.pptx,.jpg,.png',
        validation: { 
          validate: {
            docCheck: (val: any) => 
              validateDocument(val) || 'Please upload at least one Participant Recruitment Material.',
          },
        },
        allowMultiple: true, 
        dependency: { field: 'doc_selection', value: 'Participant Recruitment Materials' },
      },
      {
        itemType: 'document',
        name: 'doc_data_collection_tools',
        label: 'Data Collection <br><small>e.g., CRFs, Questionnaires, Surveys</small>',
        documentType: '.pdf,.doc,.docx',
        validation: { 
          validate: {
            docCheck: (val: any) => 
              validateDocument(val) || 'Please upload at least one Data Collection Tool.',
          },
        },
        allowMultiple: true,
        dependency: { field: 'doc_selection', value: 'Data Collection Tools (CRFs / Questionnaires /Surveys)' },
      },
      {
        itemType: 'document',
        name: 'doc_additional_files',
        label: 'Additional Files<br><small>Upload any additional supporting documents.</small>',
        documentType: '.pdf,.doc,.docx,.ppt,.pptx,.jpg,.png',
        validation: { 
          validate: {
            docCheck: (val: any) => 
              validateDocument(val) || 'Please upload at least one Additional File.',
          },
        },
        allowMultiple: true,
        dependency: { field: 'doc_selection', value: 'Additional Document Types' },
      },
    ],
  },
];

/** Dependencies check logic. */
function checkSingleDependency(
  watchValues: any,
  dependency: { field: string; value: string }
): boolean {
  const currentValue = watchValues[dependency.field];

   // Handle array values (checkboxes)
   if (Array.isArray(currentValue)) {
    return currentValue.includes(dependency.value);
  }

  // Handle single values (radio, text, etc.)

  return currentValue === dependency.value;
}

function shouldDisplayField(watchValues: any, dependency?: Record<string, any>): boolean {
  if (!dependency) return true;

  if (dependency.field && dependency.value) {
    return checkSingleDependency(watchValues, dependency as { field: string; value: string });
  }

  if (dependency.not) {
    const isSatisfied = checkSingleDependency(watchValues, dependency.not);
    return !isSatisfied;
  }

  if (dependency.all && Array.isArray(dependency.all)) {
    return dependency.all.every((cond: { field: string; value: string }) =>
      checkSingleDependency(watchValues, cond)
    );
  }

  return true;
}

/** Helper function to render HTML content safely */
function renderHTML(content: string, className?: string) {
    return (
      <span 
        className={className} 
        dangerouslySetInnerHTML={{ __html: content }}
      />
    );
  }

/** Props for FormSubmissionFields */
interface FormSubmissionFieldsProps {
  onSubmit: (data: FormData) => void;
  register: ReturnType<typeof useForm<FormData>>['register'];
  errors: FieldErrorsImpl<FormData>;
  watchAll: FormData;
  setValue: ReturnType<typeof useForm<FormData>>['setValue'];
  handleImmediateAutosave: () => void;
  handleSubmit: ReturnType<typeof useForm<FormData>>['handleSubmit'];
  savingStatus: 'idle' | 'saving' | 'saved' | 'error';
  loading: boolean;
  sectionRefs?: React.RefObject<HTMLDivElement>[];
  /** Handler for document uploads - will store files in Supabase and create database records */
  handleDocumentUpload?: (files: File | File[], fieldName: string, allowMultiple?: boolean) => Promise<any>;
  /** Handler for document deletion - will remove files from Supabase and database records */
  handleDocumentDelete?: (documentId: string, filePath: string, fieldName: string) => Promise<boolean>;
  /** Handler for document removal without deletion - will remove files from local form data */
  handleDocumentRemovalWithoutDelete?: (fieldName: string, docOrIndex: number | DocumentFile | null) => void;
  /** Object mapping field names to error messages for document uploads */
  docUploadError?: {[key: string]: string};
  /** The current submission status. Used to decide doc deletion approach. */
  submissionStatus?: string;
  /** Whether to hide the submit button */
  hideSubmitButton?: boolean;
}

/** Component that encapsulates the entire IRB form. */
export const FormSubmissionFields: React.FC<FormSubmissionFieldsProps> = ({
  onSubmit,
  register,
  errors,
  watchAll,
  setValue,
  handleImmediateAutosave,
  handleSubmit,
  savingStatus,
  loading,
  sectionRefs,
  handleDocumentUpload,
  handleDocumentDelete,
  handleDocumentRemovalWithoutDelete,
  docUploadError,
  submissionStatus,
  hideSubmitButton
}) => {
  // Add useEffect for logging errors and watchAll values
  useEffect(() => {
    console.log('Form values changed:', watchAll);
    // Log any document field values specifically
    const documentFields = Object.keys(watchAll).filter(key => key.startsWith('doc_'));
    if (documentFields.length > 0) {
      console.log('Document fields current values:');
      documentFields.forEach(field => {
        console.log(`${field}:`, watchAll[field], 
          'Type:', watchAll[field] ? typeof watchAll[field] : 'null',
          'Is array:', Array.isArray(watchAll[field]),
          'Has value:', !!watchAll[field]);
      });
    }
  }, [watchAll]);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      console.log('Form validation errors:', errors);
      // Check specifically for document-related errors
      const docErrors = Object.keys(errors).filter(key => key.startsWith('doc_'));
      if (docErrors.length > 0) {
        console.log('Document field errors:');
        docErrors.forEach(field => {
          console.log(`${field} error:`, errors[field]);
        });
      }
    }
  }, [errors]);

  // Basic styling classes
  const labelClasses = 'block text-base font-[Inter] text-[#343a40] font-medium mb-2';
  const inputClasses =
    'w-full bg-white border border-[#343a40] rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-[#F55D3E]';
  const errorClasses = 'mt-1 text-sm font-[Inter] text-[#B83EF5]';
  
  // Check if a field is part of the PI information section
  const isPIInfoField = (fieldName?: string) => {
    if (!fieldName) return false;
    const pi_billing_Fields = [
      'pi_first_name', 'pi_last_name', 'pi_degree', 'pi_email', 'pi_profile_links',
      'pi_phone', 'pi_organization', 'pi_country', 'pi_country_non-us', 'pi_address_line_1',
      'pi_address_line_2', 'pi_city', 'pi_state', 'pi_zip_code', 'pi_npi_number', 'is_pi_physician',
      'invoice_email','billing_contact_first_name','billing_contact_last_name','billing_contact_phone'
    ];
    return pi_billing_Fields.includes(fieldName);
  };

  // Get grid column classes based on field name
  const getGridColumnClass = (fieldName?: string) => {
    if (!fieldName) return 'col-span-2';
    
    // Field pairs that should be side-by-side
    if (fieldName === 'pi_first_name' || fieldName === 'pi_last_name') {
      return 'col-span-1';
    }
    if (fieldName === 'pi_degree' || fieldName === 'pi_organization') {
      return 'col-span-1';
    }
    if (fieldName === 'pi_email' || fieldName === 'pi_phone') {
      return 'col-span-1';
    }
    if (fieldName === 'pi_city' || fieldName === 'pi_state') {
      return 'col-span-1';
    }
    if (fieldName === 'pi_address_line_1' || fieldName === 'pi_address_line_2') {
      return 'col-span-1';
    }
    if (fieldName === 'pi_zip_code' || fieldName === 'pi_country') {
      return 'col-span-1';
    }
    if (fieldName === 'invoice_email' || fieldName === 'billing_contact_phone') {
      return 'col-span-1';
    }
    if (fieldName === 'billing_contact_first_name' || fieldName === 'billing_contact_last_name') {
      return 'col-span-1';
    }
    // Default to full width
    return 'col-span-2';
  };

  // Renders a single form field based on its type
  const renderField = (field: FormField) => {
    const currentValue = field.name ? watchAll[field.name] : '';

    switch (field.fieldType) {
      case 'text': {
        // Extract maxLength from validation and calculate remaining characters
        const maxLength = field.validation?.maxLength;
        const currentLength = typeof currentValue === 'string' ? currentValue.length : 0;
        
        return (
          <div className="mb-4" key={field.name}>
            <div className="flex justify-between items-center">
              <label className={labelClasses}>{renderHTML(field.label)}</label>
              {maxLength !== undefined && (
                <span className={`text-sm ${
                  currentLength > maxLength 
                    ? 'text-red-500' 
                    : currentLength > maxLength * 0.8 
                      ? 'text-amber-600' 
                      : 'text-gray-500'
                }`}>
                  {currentLength}/{maxLength}
                </span>
              )}
            </div>

            {field.rows && field.rows > 1 ? (
              <textarea
                placeholder={field.placeholder}
                className={inputClasses}
                rows={field.rows}
                {...register(field.name ?? '', field.validation)}
              />
            ) : (
              <input
                placeholder={field.placeholder}
                className={inputClasses}
                {...register(field.name ?? '', field.validation)}
              />
            )}
            {errors[field.name ?? ''] && (
              <span className={errorClasses}>Invalid input.</span>
            )}
          </div>
        );
      }
      case 'radio': {
        // Yes/No radios inline
        if (
          field.options &&
          field.options.length === 2 &&
          field.options.every(opt => ['Yes', 'No'].includes(opt))
        ) {
          return (
            <div className="mb-4 flex items-center justify-between" key={field.name}>
              <label
                className={labelClasses + ' mb-0'}
                dangerouslySetInnerHTML={{ __html: field.label }}
                style={{ flex: 1, minWidth: 0 }}
              />
              <div className="flex items-center gap-x-6" style={{ minWidth: 120, justifyContent: 'flex-end' }}>
                {field.options.map(opt => (
                  <label key={opt} className="inline-flex items-center gap-x-1">
                    <input
                      type="radio"
                      {...register(field.name ?? '', { required: field.validation?.required })}
                      value={opt}
                      checked={currentValue === opt}
                      onChange={e => {
                        setValue(field.name ?? '', opt, { shouldValidate: true });
                        handleImmediateAutosave();
                      }}
                      className="form-radio h-4 w-4"
                    />
                    <span className="text-sm">{opt}</span>
                  </label>
                ))}
              </div>
              {errors[field.name ?? ''] && (
                <span className="ml-3 text-xs text-red-500">Required.</span>
              )}
            </div>
          );
        }
        // fallback to existing rendering for other radios (leave as-is for now)
        break;
      }

      case 'dropdown-single':
        if (!field.options) return null;
        return (
          <div className="mb-6" key={field.name}>
            <Listbox
              value={currentValue || ''}
              onChange={(val: any) => {
                setValue(field.name ?? '', val, { shouldValidate: true });
                handleImmediateAutosave();
              }}
            >
              <Listbox.Label className={labelClasses}>{renderHTML(field.label)}</Listbox.Label>
              <div className="relative mt-1">
                <Listbox.Button
                  className="relative w-full cursor-pointer bg-white border border-[#343a40] rounded-md px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-[#F55D3E]"
                >
                  <span className="block truncate">{currentValue || '--Select--'}</span>
                </Listbox.Button>
                <div className="relative mt-1">
                  <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white border border-[#343a40]">
                    {field.options.map((opt) => (
                      <Listbox.Option
                        key={opt}
                        value={opt}
                        className={({ active }) =>
                          `cursor-pointer select-none relative py-2 pl-3 pr-4 ${
                            active ? 'bg-[#F9A594] text-[#343a40]' : 'text-[#343a40]'
                          }`
                        }
                      >
                        {opt}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </div>
              </div>
            </Listbox>
            {errors[field.name ?? ''] && (
              <span className={errorClasses}>Required.</span>
            )}
          </div>
        );

      case 'dropdown-multiple':
        if (!field.options) return null;
        const multiValue = Array.isArray(currentValue) ? currentValue : [];
        return (
          <div className="mb-6" key={field.name}>
            <Listbox
              multiple
              value={multiValue}
              onChange={(val: any) => {
                setValue(field.name ?? '', val, { shouldValidate: true });
                handleImmediateAutosave();
              }}
            >
              <Listbox.Label className={labelClasses}>{renderHTML(field.label)}</Listbox.Label>
              <div className="relative mt-1">
                <Listbox.Button
                  className="relative w-full cursor-pointer bg-white border border-[#343a40] rounded-md px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-[#F55D3E]"
                >
                  <span className="block truncate">
                    {multiValue.length > 0 ? multiValue.join(', ') : '--Select--'}
                  </span>
                </Listbox.Button>
                <div className="relative mt-1">
                  <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white border border-[#343a40]">
                    {field.options.map((opt) => {
                      const selected = multiValue.includes(opt);
                      return (
                        <Listbox.Option
                          key={opt}
                          value={opt}
                          className={({ active }) =>
                            `cursor-pointer select-none relative py-2 pl-3 pr-4 ${
                              active ? 'bg-[#F9A594] text-[#343a40]' : 'text-[#343a40]'
                            }`
                          }
                        >
                          <span>{opt}</span>
                          {selected && (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-green-500">
                              ✓
                            </span>
                          )}
                        </Listbox.Option>
                      );
                    })}
                  </Listbox.Options>
                </div>
              </div>
            </Listbox>
            {errors[field.name ?? ''] && (
              <span className={errorClasses}>Required.</span>
            )}
          </div>
        );

      case 'checkboxes':
        if (!field.options) return null;
        return (
          <div className="mb-6" key={field.name}>
            <label className={labelClasses}>{renderHTML(field.label)}</label>
            {field.options.map((opt) => {
              const checkedArr = watchAll[field.name ?? ''] || [];
              const isChecked = checkedArr.includes(opt);

              const handleCheckboxChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
                const valArray = Array.isArray(checkedArr) ? [...checkedArr] : [];
                if (evt.target.checked) {
                  valArray.push(opt);
                } else {
                  const idx = valArray.indexOf(opt);
                  if (idx !== -1) valArray.splice(idx, 1);
                }
                setValue(field.name ?? '', valArray, { shouldValidate: true });
                handleImmediateAutosave();
              };

              return (
                <div key={opt} className="flex items-center mt-2">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={handleCheckboxChange}
                    className="h-4 w-4 text-[#F55D3E] border-[#343a40] rounded focus:ring-0 mr-2"
                  />
                  <span className="text-base font-[Inter] text-[#343a40]">{opt}</span>
                </div>
              );
            })}
            {errors[field.name ?? ''] && (
              <span className={errorClasses}>Required.</span>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <form
      onSubmit={(e) => {
        console.log('Form submission attempt with values:', watchAll);
        console.log('Current errors:', errors);
        if (Object.keys(errors).length > 0) {
          // Create a mapping of field names to their labels
          const nameToLabelMap: Record<string, string> = {};
          
          // Iterate through all form sections and fields to build the mapping
          formSections.forEach(section => {
            section.items.forEach(field => {
              if (field.name) {
                nameToLabelMap[field.name] = field.label;
              }
            });
          });
          
          // Map error field names to their labels, fallback to field name if label not found
          const errorLabels = Object.keys(errors).map(fieldName => 
            nameToLabelMap[fieldName] || fieldName
          );
          
          alert(`Please complete the following required fields:\n${errorLabels.join('\n')}`);
        }
        return handleSubmit(onSubmit)(e);
      }}
      className="bg-[#f8f9fa] p-6 rounded-md max-w-4xl mx-auto my-8 font-[Inter]"
    >
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#F55D3E] mb-2">New Study Submission Form</h1>
        <p className="text-sm text-[#343a40]">
          This form shouldn't take more than 10 minutes to complete if you have study information ready. 
          We'll continuously save your progress as you're working, and you may access this submission from your dashboard if you exit this page.
        </p>
      </div>

      {formSections.map((section, sIdx) => (
        <div 
          key={sIdx} 
          className="mb-8 pt-[80px] mt-[-80px]"
          ref={sectionRefs ? sectionRefs[sIdx] : null}
        >
          {section.heading && (
            <h2 className="text-xl font-semibold text-[#F55D3E] mb-4">{section.heading}</h2>
          )}
          {section.description && (
            <p className="text-base text-[#343a40] mb-4 whitespace-pre-line">{section.description}</p>
          )}
          {/* Check if this section has PI info fields and wrap in grid if needed */}
          {section.items.some(item => item.name && isPIInfoField(item.name)) && (
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-2 mb-4">
              {section.items.map((item, iIdx) => {
                if (!shouldDisplayField(watchAll, item.dependency) || !isPIInfoField(item.name)) {
                  return null;
                }
                
                if (item.itemType === 'question' && item.name) {
                  return (
                    <div key={item.name} className={`${getGridColumnClass(item.name)} mb-2`}>
                      {renderField(item)}
                    </div>
                  );
                }
                return null;
              })}
            </div>
          )}
          
          {/* Regular (non-PI) fields */}
          {section.items.map((item, iIdx) => {
            if (!shouldDisplayField(watchAll, item.dependency) || (item.name && isPIInfoField(item.name))) {
              return null;
            }
            if (item.itemType === 'question' && item.name) {
              return renderField(item);
            } else if (item.itemType === 'warning') {
              return (
                <div
                  key={`${sIdx}-${iIdx}`}
                  className="border-l-4 border-[#B83EF5] bg-[#f8f9fa] p-4 mb-6"
                >
                  <strong className="text-[#B83EF5] text-2xl">⚠️</strong>{' '}
                  <span className="text-[#343a40]">{renderHTML(item.label)}</span>
                </div>
              );
            } else if (item.itemType === 'instruction') {
              return (
                <div
                  key={`${sIdx}-${iIdx}`}
                  className="border-l-4 border-[#F9A594] bg-white p-4 mb-6"
                >
                  <strong className="text-[#343a40] text-2xl">ℹ</strong>{' '}
                  <span className="text-[#343a40]">{renderHTML(item.label)}</span>
                </div>
              );
            } else if (item.itemType === 'document' && item.name) {
              const currentValue = watchAll[item.name ?? ''];
              console.log(`Rendering document field ${item.name}:`, currentValue);
              
              // Check if a valid doc is recognized
              const isFileUploaded = validateDocument(currentValue);
              
              console.log(`Document field ${item.name} isFileUploaded:`, isFileUploaded);
              console.log(`Document field ${item.name} validation:`, item.validation);
              
              return (
                <div className="mb-6" key={item.name}>
                  <div className="flex justify-between items-center">
                    <label className={labelClasses}>{renderHTML(item.label)}</label>
                  </div>
                  
                  <div className="mt-2">
                    <div className="flex items-center justify-center w-full">
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-white border-[#343a40] hover:bg-gray-50">
                        {!isFileUploaded ? (
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <svg className="w-8 h-8 mb-4 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
                            </svg>
                            <p className="mb-2 text-sm text-gray-500">
                              <span className="font-semibold">Click to upload</span> or drag and drop
                            </p>
                            <p className="text-xs text-gray-500">{item.documentType || 'PDF, DOC, DOCX, RTF'}</p>
                            {item.allowMultiple && <p className="text-xs text-blue-500">You can upload multiple files</p>}
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            {Array.isArray(currentValue) ? (
                              <>
                                <svg className="w-8 h-8 mb-4 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <p className="mb-2 text-sm text-gray-500">
                                  <span className="font-semibold">{currentValue.length} files uploaded</span>
                                </p>
                                <div className="max-h-20 overflow-auto">
                                  {currentValue.map((file: DocumentFile, idx: number) => (
                                    <div key={idx} className="flex items-center justify-between text-xs">
                                      <span className="truncate max-w-[200px]">
                                        {file.name || file.filename}
                                        {file.isUploading && (
                                          <span className="ml-1 text-blue-500">(uploading...)</span>
                                        )}
                                      </span>
                                      <button 
                                        type="button"
                                        className="text-xs text-red-500 hover:text-red-700 ml-2"
                                        onClick={async (e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          
                                          if (submissionStatus === 'DRAFT') {
                                            // Original implementation for DRAFT status
                                            // If file has doc metadata with ID, we attempt a deletion
                                            if (handleDocumentDelete && file.id && file.file_path) {
                                              const success = await handleDocumentDelete(
                                                file.id, 
                                                file.file_path,
                                                item.name ?? ''
                                              );
                                              
                                              if (success) {
                                                const updatedFiles = [...currentValue];
                                                updatedFiles.splice(idx, 1);
                                                setValue(item.name ?? '', updatedFiles.length > 0 ? updatedFiles : null, { shouldValidate: true });
                                                handleImmediateAutosave();
                                              }
                                            } else {
                                              // Just remove from local state if not yet uploaded
                                              const updatedFiles = [...currentValue];
                                              updatedFiles.splice(idx, 1);
                                              setValue(item.name ?? '', updatedFiles.length > 0 ? updatedFiles : null, { shouldValidate: true });
                                              handleImmediateAutosave();
                                            }
                                          } else if (submissionStatus === 'NEEDS_ACTION') {
                                            // For NEEDS_ACTION status, use handleDocumentRemovalWithoutDelete
                                            if (handleDocumentRemovalWithoutDelete) {
                                              // Pass the document object directly - the function will handle finding it in the array
                                              handleDocumentRemovalWithoutDelete(item.name ?? '', file);
                                            }
                                          }
                                        }}
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </>
                            ) : (
                              <>
                                <svg className="w-8 h-8 mb-4 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <p className="mb-2 text-sm text-gray-500">
                                  <span className="font-semibold">File uploaded</span>: {currentValue.name || currentValue.filename}
                                  {currentValue.isUploading && (
                                    <span className="ml-1 text-blue-500">(uploading...)</span>
                                  )}
                                </p>
                                <button 
                                  type="button"
                                  className="text-xs text-red-500 hover:text-red-700"
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    const docFile = currentValue as DocumentFile;
                                    
                                    if (submissionStatus === 'DRAFT') {
                                      // For DRAFT status, attempt to delete from Supabase if we have doc metadata
                                      if (handleDocumentDelete && docFile.id && docFile.file_path) {
                                        const success = await handleDocumentDelete(
                                          docFile.id,
                                          docFile.file_path,
                                          item.name ?? ''
                                        );
                                        
                                        if (success) {
                                          setValue(item.name ?? '', null, { shouldValidate: true });
                                          handleImmediateAutosave();
                                        }
                                      } else {
                                        // Just remove from local state if not yet uploaded
                                        setValue(item.name ?? '', null, { shouldValidate: true });
                                        handleImmediateAutosave();
                                      }
                                    } else if (submissionStatus === 'NEEDS_ACTION') {
                                      // For NEEDS_ACTION status, call the handleRemoveDocumentFromState function
                                      if (handleDocumentRemovalWithoutDelete) {
                                        // Pass the document object directly - the function will handle it
                                        handleDocumentRemovalWithoutDelete(item.name ?? '', docFile);
                                      }
                                    }
                                  }}
                                >
                                  Remove file
                                </button>
                              </>
                            )}
                          </div>
                        )}
                        <input 
                          id={item.name}
                          type="file" 
                          className="hidden"
                          multiple={item.allowMultiple === true}
                          accept={item.documentType || '.pdf,.doc,.docx,.rtf'}
                          {...register(item.name ?? '', {
                            ...item.validation,
                            onChange: async (e) => {
                              if (item.allowMultiple) {
                                const files = Array.from(e.target.files || []) as File[];
                                if (files.length > 0) {
                                  if (handleDocumentUpload) {
                                    // Show files with isUploading before we call actual upload
                                    const tempFiles = files.map((file) => ({
                                      name: file.name,
                                      size: file.size,
                                      type: file.type,
                                      lastModified: file.lastModified,
                                      isUploading: true
                                    }));
                                    
                                    if (Array.isArray(currentValue) && currentValue.length > 0) {
                                      setValue(item.name ?? '', [...currentValue, ...tempFiles], { shouldValidate: true });
                                    } else {
                                      setValue(item.name ?? '', tempFiles, { shouldValidate: true });
                                    }
                                    
                                    // Upload the documents
                                    const uploadedDocs = await handleDocumentUpload(
                                      files,
                                      item.name ?? '',
                                      true
                                    );
                                    if (uploadedDocs) {
                                      // Replace temp with real doc metadata
                                      const currentFiles = Array.isArray(currentValue) ? [...currentValue] : [];
                                      const newFiles = currentFiles.filter(f => !f.isUploading).concat(uploadedDocs);
                                      setValue(item.name ?? '', newFiles, { shouldValidate: true });
                                    } else {
                                      const currentFiles = Array.isArray(currentValue) ? [...currentValue] : [];
                                      const newFiles = currentFiles.filter(f => !f.isUploading);
                                      setValue(item.name ?? '', newFiles.length > 0 ? newFiles : null, { shouldValidate: true });
                                    }
                                  } else {
                                    // If no upload handler, store files locally
                                    if (Array.isArray(currentValue) && currentValue.length > 0) {
                                      setValue(item.name ?? '', [...currentValue, ...files], { shouldValidate: true });
                                    } else {
                                      setValue(item.name ?? '', files, { shouldValidate: true });
                                    }
                                  }
                                  handleImmediateAutosave();
                                }
                              } else {
                                // Single file
                                const file = e.target.files?.[0] as File;
                                if (file) {
                                  const tempFile = {
                                    name: file.name,
                                    size: file.size,
                                    type: file.type,
                                    lastModified: file.lastModified,
                                    isUploading: true
                                  };
                                  setValue(item.name ?? '', tempFile, { shouldValidate: true });
                                  
                                  // If we have a doc handler, do actual upload
                                  if (handleDocumentUpload) {
                                    const uploadedDoc = await handleDocumentUpload(
                                      file,
                                      item.name ?? '',
                                      false
                                    );
                                    
                                    if (uploadedDoc) {
                                      setValue(item.name ?? '', uploadedDoc, { shouldValidate: true });
                                    } else {
                                      setValue(item.name ?? '', null, { shouldValidate: true });
                                    }
                                  } else {
                                    setValue(item.name ?? '', file, { shouldValidate: true });
                                  }
                                  handleImmediateAutosave();
                                }
                              }
                            }
                          })}
                        />
                      </label>
                    </div>
                  </div>
                  {item.itemType === 'document' && (
                    <>
                      {item.validation?.required && errors[item.name as string] && !validateDocument(currentValue) && (
                        <p className="text-sm text-red-600 mt-1">
                          This document is required.
                        </p>
                      )}
                      {docUploadError && docUploadError[item.name as string] && (
                        <p className="text-sm text-red-600 mt-1">
                          {docUploadError[item.name as string]}
                        </p>
                      )}
                    </>
                  )}
                  {docUploadError && docUploadError[item.name ?? ''] && (
                    <span className={errorClasses}>
                      {docUploadError[item.name ?? '']}
                    </span>
                  )}
                </div>
              );
            }
            return null;
          })}
        </div>
      ))}

      {/* Only render the submit button if not hidden */}
      {!hideSubmitButton && (
        <button
          type="submit"
          className="inline-flex items-center justify-center bg-[#F55D3E] text-white rounded-md px-4 py-2 font-semibold hover:bg-[#d94a30] focus:outline-none focus:ring-2 focus:ring-[#F55D3E]"
        >
          Submit
        </button>
      )}
    </form>
  );
};
